var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Indicator editor restricted by the MARC catalog (TASK-181 WP-C CP-3;
 * rewired onto the live catalog in CZNA-369).
 *
 * For tags known in the MARC catalog the option values are constrained to
 * the catalog's allowed set (e.g. 100 → ind1 ∈ {0,1,3}). For tags outside
 * the catalog (user-defined extension tags) — and while the catalog is
 * still loading, or if the fetch failed — all current options remain
 * editable. The "Tag X není v MARC katalogu" tooltip only appears once
 * catalog data has actually loaded AND the tag is genuinely absent from
 * it, so it neither flashes during the initial fetch nor misfires for
 * every standard tag when the backend endpoint is down (loading and error
 * both leave `catalog` undefined, so gating on `catalog` truthiness covers
 * both in one condition).
 *
 * The editor produces an entire IndicatorDef update — dispatch action
 * `updateIndicator` with `which` and the full IndicatorDef replacement
 * (we keep `defaultValue`, options array, and any other property the
 * reducer doesn't mutate).
 */
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { TextField, Select, Button } from '@folio/stripes/components';
import { readCsLabel, writeCsLabel } from './labelUtils';
import { getAllowedIndicatorValues, getMarcField } from './useMarcCatalog';
import { useMarcCatalog } from '../../hooks/useMarcCatalog';
import { errorPathToVisualLocator } from './utils/errorPathToVisualLocator';
import { isActionableError, humanizeMessage } from './humanizeError';
var IndicatorEditor = function (_a) {
    var tabIdx = _a.tabIdx, fieldIdx = _a.fieldIdx, fieldTag = _a.fieldTag, which = _a.which, indicator = _a.indicator, validationErrors = _a.validationErrors, dispatch = _a.dispatch;
    var intl = useIntl();
    // React-query dedupes by queryKey, so each IndicatorEditor instance
    // (up to 2 render at once — ind1/ind2 of the selected field) calling
    // this independently is cheap: one network fetch, cached 1h, shared
    // across instances. Threading the catalog down from
    // FormConfigVisualEditor/FieldPropertyPanel would mean plumbing a new
    // prop through a component that has no other reason to know about the
    // MARC catalog — calling the hook here keeps that dependency local.
    var catalog = useMarcCatalog().data;
    var allowedValues = getAllowedIndicatorValues(catalog, fieldTag, which);
    var catalogField = getMarcField(catalog, fieldTag);
    var isInCatalog = catalogField !== null;
    var isRestricted = allowedValues !== null;
    // Effective set: if catalog defines a restriction, use catalog values;
    // otherwise allow the union of current option values
    var effectiveValues = isRestricted
        ? allowedValues
        : indicator.options.map(function (opt) { return opt.value; });
    var dispatchUpdate = function (next) {
        dispatch({ type: 'updateIndicator', tabIdx: tabIdx, fieldIdx: fieldIdx, which: which, indicator: next });
    };
    var updateLabel = function (newLabel) {
        dispatchUpdate(__assign(__assign({}, indicator), { label: writeCsLabel(indicator.label, newLabel) }));
    };
    var updateDefault = function (val) {
        dispatchUpdate(__assign(__assign({}, indicator), { defaultValue: val }));
    };
    var updateOption = function (idx, partial) {
        dispatchUpdate(__assign(__assign({}, indicator), { options: indicator.options.map(function (opt, i) { return (i === idx ? __assign(__assign({}, opt), partial) : opt); }) }));
    };
    var addOption = function () {
        // Pick the first allowed value not yet in options
        var used = new Set(indicator.options.map(function (o) { return o.value; }));
        var next = effectiveValues.find(function (v) { return !used.has(v); });
        if (next === undefined)
            return;
        dispatchUpdate(__assign(__assign({}, indicator), { options: __spreadArray(__spreadArray([], indicator.options, true), [{ value: next, label: '' }], false) }));
    };
    var removeOption = function (idx) {
        dispatchUpdate(__assign(__assign({}, indicator), { options: indicator.options.filter(function (_, i) { return i !== idx; }) }));
    };
    var indicatorErrors = validationErrors.filter(isActionableError).filter(function (err) {
        var loc = errorPathToVisualLocator(err.path);
        return loc.tabIdx === tabIdx && loc.fieldIdx === fieldIdx && loc.indicator === which;
    });
    return (React.createElement("div", { "data-testid": "indicator-editor-".concat(which), style: { marginTop: '0.5rem', padding: '0.4rem', border: '1px solid #ccc', borderRadius: '4px' } },
        React.createElement("h4", { style: { marginTop: 0 } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.property.indicatorHeading", values: { which: which } })),
        catalog && !isInCatalog && (React.createElement("div", { "data-testid": "indicator-editor-".concat(which, "-not-in-catalog"), style: { fontSize: '0.8rem', color: '#c80', marginBottom: '0.25rem' } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.property.tagNotInCatalog", values: { tag: fieldTag } }))),
        React.createElement(TextField, { value: readCsLabel(indicator.label), onChange: function (e) { return updateLabel(e.target.value); }, label: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.property.indicatorLabel' }), "data-testid": "indicator-editor-".concat(which, "-label-input") }),
        React.createElement(TextField, { value: indicator.defaultValue, onChange: function (e) { return updateDefault(e.target.value); }, label: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.property.indicatorDefault' }), "data-testid": "indicator-editor-".concat(which, "-default-input") }),
        React.createElement("div", { "data-testid": "indicator-editor-".concat(which, "-options") }, indicator.options.map(function (opt, idx) { return (React.createElement("div", { key: "".concat(opt.value, "-").concat(idx), "data-testid": "indicator-editor-".concat(which, "-option-").concat(idx), style: { display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.25rem' } },
            React.createElement(Select, { value: opt.value, onChange: function (e) { return updateOption(idx, { value: e.target.value }); }, dataOptions: effectiveValues.map(function (v) { return ({ value: v, label: v }); }), "data-testid": "indicator-editor-".concat(which, "-option-").concat(idx, "-value") }),
            React.createElement(TextField, { value: readCsLabel(opt.label), onChange: function (e) { return updateOption(idx, { label: writeCsLabel(opt.label, e.target.value) }); }, "data-testid": "indicator-editor-".concat(which, "-option-").concat(idx, "-label") }),
            React.createElement("button", { type: "button", "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.property.removeIndicatorOptionAria' }, { value: opt.value }), title: indicator.options.length === 1
                    ? intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.property.removeIndicatorOptionLastTitle' })
                    : undefined, disabled: indicator.options.length === 1, style: indicator.options.length === 1 ? { opacity: 0.4 } : undefined, onClick: function () { return removeOption(idx); }, "data-testid": "indicator-editor-".concat(which, "-option-").concat(idx, "-remove") }, "\u2715"))); })),
        React.createElement(Button, { onClick: addOption, disabled: indicator.options.length >= effectiveValues.length, "data-testid": "indicator-editor-".concat(which, "-add-option"), marginBottom0: true },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.property.addOption" })),
        indicatorErrors.length > 0 && (React.createElement("div", { "data-testid": "indicator-editor-".concat(which, "-errors"), style: { marginTop: '0.25rem' } }, indicatorErrors.map(function (err, i) { return (React.createElement("div", { key: i, style: { color: '#c00', fontSize: '0.8rem' } }, humanizeMessage(err.message, err.path))); })))));
};
export default IndicatorEditor;
